import { useState, useRef, useEffect, useCallback } from "react";
import { Muxer, ArrayBufferTarget } from "mp4-muxer";

const STAR_COUNT = 250;
const STARS = Array.from({ length: STAR_COUNT }, () => ({
  x: Math.random(),
  y: Math.random(),
  r: Math.random() * 1.0 + 0.2,
  o: Math.random() * 0.5 + 0.1,
}));

function rushInto(t) {
  return t * t * t;
}
function rushFrom(t) {
  return 1 - Math.pow(1 - t, 3);
}

const ANIM_DUR = 3500;

// Font caps were tuned against the ~540px display canvas; express them as a
// fraction of the render width so text keeps the same scale at any resolution.
const FONT_REF_W = 540;
const CONCEPT_FONT_RATIO = 32 / FONT_REF_W;
const TECH_FONT_RATIO = 26 / FONT_REF_W;

// Download tiers — each produces a video at the given square pixel width
// (MP4 where WebCodecs or iOS Safari supports it, WebM as fallback).
const QUALITIES = [
  { key: "sd", w: 480 },
  { key: "hd", w: 720 },
];

export default function FissionApp() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const [concept1, setConcept1] = useState("Human");
  const [concept2, setConcept2] = useState("Language");
  const [tech, setTech] = useState("AI");
  const [phase, setPhase] = useState("idle");
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [encodeProgress, setEncodeProgress] = useState(null);
  const [encodingKey, setEncodingKey] = useState(null);
  const cachedExportRef = useRef({});
  const inFlightExportRef = useRef({});
  const exportSessionRef = useRef(0);

  const st = useRef({
    cR: 0, tR: 0,
    c1x: 0, c1y: 0, c2x: 0, c2y: 0, tx: 0, ty: 0,
    c1o: 0, c2o: 0, to: 0,
    c1to: 0, c2to: 0, tto: 0,
    c1s: 1, c2s: 1, ts: 1,
  });

  const drawGlowCircle = useCallback((ctx, x, y, radius, opacity, scale) => {
    const r = radius * scale;
    const offX = -r * 0.2;
    const offY = -r * 0.2;

    const outer = ctx.createRadialGradient(x + offX, y + offY, r * 0.1, x, y, r * 1.35);
    outer.addColorStop(0, `rgba(255,255,255,${opacity * 0.3})`);
    outer.addColorStop(0.4, `rgba(230,230,245,${opacity * 0.15})`);
    outer.addColorStop(0.7, `rgba(180,180,210,${opacity * 0.06})`);
    outer.addColorStop(1, `rgba(100,100,150,0)`);
    ctx.beginPath();
    ctx.arc(x, y, r * 1.35, 0, Math.PI * 2);
    ctx.fillStyle = outer;
    ctx.fill();

    const main = ctx.createRadialGradient(x + offX, y + offY, 0, x, y, r);
    main.addColorStop(0, `rgba(255,255,255,${opacity})`);
    main.addColorStop(0.35, `rgba(255,255,255,${opacity * 0.98})`);
    main.addColorStop(0.65, `rgba(248,248,252,${opacity * 0.93})`);
    main.addColorStop(0.85, `rgba(230,230,242,${opacity * 0.8})`);
    main.addColorStop(0.95, `rgba(200,200,225,${opacity * 0.5})`);
    main.addColorStop(1, `rgba(160,160,195,${opacity * 0.15})`);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = main;
    ctx.fill();
  }, []);

  const drawLabel = useCallback((ctx, text, x, y, opacity, maxFontSize, maxWidth) => {
    if (opacity <= 0.01) return;
    const fontStr = (size) => `700 ${size}px "Segoe UI", "Helvetica Neue", Arial, sans-serif`;

    // Build 1/2/3-line layout candidates (each picks the most-balanced split).
    // For each candidate, find the largest font size that fits maxWidth.
    // Then pick the candidate with the biggest font; ties go to fewer lines.
    const words = text.split(/\s+/).filter(Boolean);
    const candidates = [{ lines: [text] }];
    ctx.font = fontStr(maxFontSize);
    if (words.length >= 2) {
      let best = null;
      let bestMax = Infinity;
      for (let i = 1; i < words.length; i++) {
        const a = words.slice(0, i).join(" ");
        const b2 = words.slice(i).join(" ");
        const m = Math.max(ctx.measureText(a).width, ctx.measureText(b2).width);
        if (m < bestMax) { bestMax = m; best = [a, b2]; }
      }
      if (best) candidates.push({ lines: best });
    }
    if (words.length >= 3) {
      let best = null;
      let bestMax = Infinity;
      for (let i = 1; i < words.length - 1; i++) {
        for (let j = i + 1; j < words.length; j++) {
          const a = words.slice(0, i).join(" ");
          const b2 = words.slice(i, j).join(" ");
          const c = words.slice(j).join(" ");
          const m = Math.max(
            ctx.measureText(a).width,
            ctx.measureText(b2).width,
            ctx.measureText(c).width,
          );
          if (m < bestMax) { bestMax = m; best = [a, b2, c]; }
        }
      }
      if (best) candidates.push({ lines: best });
    }
    candidates.forEach((opt) => {
      let size = maxFontSize;
      while (size > 8) {
        ctx.font = fontStr(size);
        const w = Math.max(...opt.lines.map((l) => ctx.measureText(l).width));
        if (w <= maxWidth) break;
        size -= 1;
      }
      opt.fontSize = size;
    });
    candidates.sort((a, b) => b.fontSize - a.fontSize || a.lines.length - b.lines.length);
    const fontSize = candidates[0].fontSize;
    const lines = candidates[0].lines;

    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.font = fontStr(fontSize);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = `rgba(10,10,15,${opacity})`;
    const lineHeight = fontSize * 1.05;
    const startY = y - ((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, i) => {
      ctx.fillText(line, x, startY + i * lineHeight + fontSize * 0.05);
    });
    ctx.restore();
  }, []);

  const paint = useCallback((ctx, w, h, sArg) => {
    const s = sArg || st.current;
    ctx.clearRect(0, 0, w, h);

    const bg = ctx.createRadialGradient(w * 0.5, h * 0.45, 0, w * 0.5, h * 0.5, w * 0.75);
    bg.addColorStop(0, "#0c0c1a");
    bg.addColorStop(0.5, "#070712");
    bg.addColorStop(1, "#030308");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    STARS.forEach((star) => {
      ctx.beginPath();
      ctx.arc(star.x * w, star.y * h, star.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${star.o})`;
      ctx.fill();
    });

    if (s.c1o > 0.01) {
      drawGlowCircle(ctx, s.c1x, s.c1y, s.cR, s.c1o, s.c1s);
      drawLabel(ctx, concept1, s.c1x, s.c1y, s.c1to, Math.min(s.cR * 0.38, w * CONCEPT_FONT_RATIO), s.cR * 1.5);
    }
    if (s.c2o > 0.01) {
      drawGlowCircle(ctx, s.c2x, s.c2y, s.cR, s.c2o, s.c2s);
      drawLabel(ctx, concept2, s.c2x, s.c2y, s.c2to, Math.min(s.cR * 0.38, w * CONCEPT_FONT_RATIO), s.cR * 1.5);
    }
    if (s.to > 0.01) {
      drawGlowCircle(ctx, s.tx, s.ty, s.tR, s.to, s.ts);
      drawLabel(ctx, tech, s.tx, s.ty, s.tto, Math.min(s.tR * 0.45, w * TECH_FONT_RATIO), s.tR * 1.5);
    }
  }, [concept1, concept2, tech, drawGlowCircle, drawLabel]);

  const initState = useCallback((w, h) => {
    const s = st.current;
    s.cR = w * 0.16;
    s.tR = s.cR * 0.9;
    const overlap = s.cR * 0.15;
    s.c1x = w / 2 - s.cR + overlap;
    s.c1y = h * 0.72;
    s.c2x = w / 2 + s.cR - overlap;
    s.c2y = h * 0.72;
    s.tx = w / 2;
    s.ty = h * 0.27;
    s.c1o = 0; s.c2o = 0; s.to = 0;
    s.c1to = 0; s.c2to = 0; s.tto = 0;
    s.c1s = 1; s.c2s = 1; s.ts = 1;
  }, []);

  const computeAnchors = useCallback((w, h) => {
    const cR = w * 0.16;
    const tR = cR * 0.9;
    const overlap = cR * 0.15;
    const oC1x = w / 2 - cR + overlap;
    const oC1y = h * 0.72;
    const oC2x = w / 2 + cR - overlap;
    const oC2y = h * 0.72;
    const oTx = w / 2;
    const oTy = h * 0.27;
    const splitDist = w * 0.18;
    return {
      cR, tR,
      oC1x, oC1y, oC2x, oC2y, oTx, oTy,
      impactTy: oC1y,
      endC1x: oC1x - splitDist,
      endC2x: oC2x + splitDist,
    };
  }, []);

  // Writes deterministic state for normalized time t∈[0,1] into s.
  // Used by both the visible animation and the offscreen export.
  const applyProgress = useCallback((s, t, A) => {
    s.cR = A.cR; s.tR = A.tR;
    s.c1o = 1; s.c1to = 1;
    s.c2o = 1; s.c2to = 1;
    s.to = 1; s.tto = 1;
    s.c1s = 1; s.c2s = 1; s.ts = 1;
    s.c1x = A.oC1x; s.c1y = A.oC1y;
    s.c2x = A.oC2x; s.c2y = A.oC2y;
    s.tx = A.oTx; s.ty = A.oTy;

    const techRange = [0.0, 0.38];
    const splitRange = [0.20, 0.58];
    const pr = (t, r) => t < r[0] ? 0 : t > r[1] ? 1 : (t - r[0]) / (r[1] - r[0]);
    // Back-eased In/Out with asymmetric factors: stronger anticipation lift,
    // gentler landing rebound.
    const backInOut = (t) => {
      const cIn = 1.0, cOut = 0.25;
      if (t < 0.5) {
        const k = cIn * 1.525;
        return (Math.pow(2 * t, 2) * ((k + 1) * 2 * t - k)) / 2;
      }
      const k = cOut * 1.525;
      return (Math.pow(2 * t - 2, 2) * ((k + 1) * (t * 2 - 2) + k) + 2) / 2;
    };
    const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);

    const pTech = pr(t, techRange);
    if (pTech > 0) {
      s.ty = A.oTy + (A.impactTy - A.oTy) * backInOut(pTech);
    }
    const pSplit = pr(t, splitRange);
    if (pSplit > 0) {
      const sp = easeOutQuint(pSplit);
      s.c1x = A.oC1x + (A.endC1x - A.oC1x) * sp;
      s.c2x = A.oC2x + (A.endC2x - A.oC2x) * sp;
    }
  }, []);

  const play = useCallback(() => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    initState(w, h);
    setPhase("playing");

    const anchors = computeAnchors(w, h);
    const t0 = performance.now();

    const tick = (now) => {
      const t = Math.min((now - t0) / ANIM_DUR, 1);
      applyProgress(st.current, t, anchors);
      paint(ctx, w, h);
      if (t < 1) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        setPhase("done");
      }
    };

    animRef.current = requestAnimationFrame(tick);
  }, [paint, initState, computeAnchors, applyProgress]);

  const exportMp4 = useCallback(async (targetW, onProgress) => {
    if (typeof window.VideoEncoder === "undefined" || typeof window.VideoFrame === "undefined") {
      throw new Error("MP4 export needs WebCodecs (try a recent Chrome/Edge/Safari)");
    }
    const canvas = canvasRef.current;
    if (!canvas) throw new Error("no canvas");
    const srcW = canvas.width;
    const srcH = canvas.height;
    const fps = 30;
    // H.264 requires even dimensions.
    const w = targetW - (targetW % 2);
    const h = Math.round((w * srcH) / srcW);
    const evenH = h - (h % 2);
    const totalFrames = Math.max(1, Math.ceil((ANIM_DUR / 1000) * fps));

    // Try codecs from most-compatible (H.264 high) down to AV1/VP9 fallbacks
    // until one is accepted by the browser's encoder.
    const candidates = [
      { codec: "avc1.640034", muxer: "avc" },
      { codec: "avc1.640033", muxer: "avc" },
      { codec: "avc1.4D4032", muxer: "avc" },
      { codec: "avc1.42E032", muxer: "avc" },
      { codec: "av01.0.08M.08", muxer: "av1" },
      { codec: "vp09.00.31.08", muxer: "vp9" },
    ];
    let chosen = null;
    for (const c of candidates) {
      try {
        const r = await window.VideoEncoder.isConfigSupported({
          codec: c.codec, width: w, height: evenH, bitrate: 10_000_000, framerate: fps,
          latencyMode: "realtime",
        });
        if (r.supported) { chosen = c; break; }
      } catch (_) { /* try next */ }
    }
    if (!chosen) throw new Error("No supported MP4 codec in this browser");

    const muxer = new Muxer({
      target: new ArrayBufferTarget(),
      video: { codec: chosen.muxer, width: w, height: evenH },
      fastStart: "in-memory",
    });

    let completed = 0;
    let encoderError = null;
    const encoder = new window.VideoEncoder({
      output: (chunk, meta) => {
        muxer.addVideoChunk(chunk, meta);
        completed += 1;
        onProgress?.(completed / totalFrames);
      },
      error: (e) => { encoderError = e; },
    });
    encoder.configure({
      codec: chosen.codec, width: w, height: evenH, bitrate: 10_000_000, framerate: fps,
      latencyMode: "realtime",
    });

    const offscreen = document.createElement("canvas");
    offscreen.width = w;
    offscreen.height = evenH;
    const octx = offscreen.getContext("2d");

    const local = {
      cR: 0, tR: 0,
      c1x: 0, c1y: 0, c2x: 0, c2y: 0, tx: 0, ty: 0,
      c1o: 0, c2o: 0, to: 0,
      c1to: 0, c2to: 0, tto: 0,
      c1s: 1, c2s: 1, ts: 1,
    };
    const anchors = computeAnchors(w, evenH);

    for (let i = 0; i < totalFrames; i++) {
      if (encoderError) throw encoderError;
      const t = totalFrames === 1 ? 1 : i / (totalFrames - 1);
      applyProgress(local, t, anchors);
      paint(octx, w, evenH, local);
      const frame = new window.VideoFrame(offscreen, {
        timestamp: Math.round((i * 1e6) / fps),
        duration: Math.round(1e6 / fps),
      });
      // Every frame as a keyframe: no inter-frame deps means no DTS
      // reordering, which dodges a Chrome/Linux H.264 encoder bug.
      encoder.encode(frame, { keyFrame: true });
      frame.close();
      if ((i & 7) === 7) await new Promise((r) => setTimeout(r, 0));
    }

    await encoder.flush();
    if (encoderError) throw encoderError;
    muxer.finalize();
    onProgress?.(1);
    return {
      blob: new Blob([muxer.target.buffer], { type: "video/mp4" }),
      ext: "mp4",
    };
  }, [paint, computeAnchors, applyProgress]);

  // MediaRecorder fallback for browsers without WebCodecs (older iOS, most
  // Android). Real-time only: drives the animation on an offscreen canvas
  // while MediaRecorder samples it. Output mime depends on the browser:
  // iOS Safari → MP4, Android Chrome / Firefox → WebM.
  const exportRecorder = useCallback(async (targetW, onProgress) => {
    if (typeof window.MediaRecorder === "undefined") {
      throw new Error("Video export not supported in this browser");
    }
    const canvas = canvasRef.current;
    if (!canvas) throw new Error("no canvas");
    const srcW = canvas.width;
    const srcH = canvas.height;
    const fps = 30;
    const w = targetW;
    const h = Math.round((w * srcH) / srcW);

    const mimes = [
      "video/mp4;codecs=avc1.42E01F",
      "video/mp4;codecs=h264",
      "video/mp4",
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
    ];
    const mime = mimes.find((m) => window.MediaRecorder.isTypeSupported?.(m));
    if (!mime) throw new Error("No supported video format in this browser");
    const ext = mime.startsWith("video/mp4") ? "mp4" : "webm";

    const offscreen = document.createElement("canvas");
    offscreen.width = w;
    offscreen.height = h;
    const octx = offscreen.getContext("2d");

    const local = {
      cR: 0, tR: 0,
      c1x: 0, c1y: 0, c2x: 0, c2y: 0, tx: 0, ty: 0,
      c1o: 0, c2o: 0, to: 0,
      c1to: 0, c2to: 0, tto: 0,
      c1s: 1, c2s: 1, ts: 1,
    };
    const anchors = computeAnchors(w, h);
    applyProgress(local, 0, anchors);
    paint(octx, w, h, local);

    const stream = offscreen.captureStream(fps);
    const chunks = [];
    const recorder = new window.MediaRecorder(stream, {
      mimeType: mime,
      videoBitsPerSecond: 10_000_000,
    });
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
    const stopped = new Promise((res) => { recorder.onstop = res; });
    recorder.start();

    await new Promise((resolve) => {
      const t0 = performance.now();
      const tick = (now) => {
        const elapsed = now - t0;
        const p = Math.min(elapsed / ANIM_DUR, 1);
        applyProgress(local, p, anchors);
        paint(octx, w, h, local);
        onProgress?.(p);
        if (p < 1) requestAnimationFrame(tick);
        else resolve();
      };
      requestAnimationFrame(tick);
    });

    recorder.stop();
    stream.getTracks().forEach((t) => t.stop());
    await stopped;
    return { blob: new Blob(chunks, { type: mime }), ext };
  }, [paint, computeAnchors, applyProgress]);

  const exportVideo = useCallback(async (targetW, onProgress) => {
    if (typeof window.VideoEncoder !== "undefined" && typeof window.VideoFrame !== "undefined") {
      try {
        return await exportMp4(targetW, onProgress);
      } catch (e) {
        // WebCodecs missing a usable codec — fall back to MediaRecorder.
        onProgress?.(0);
      }
    }
    return exportRecorder(targetW, onProgress);
  }, [exportMp4, exportRecorder]);

  const flashStatus = useCallback((msg) => {
    setStatusMsg(msg);
    setTimeout(() => setStatusMsg(""), 2500);
  }, []);

  const invalidateExportCache = useCallback(() => {
    cachedExportRef.current = {};
    exportSessionRef.current += 1;
    inFlightExportRef.current = {};
    setEncodeProgress(null);
    setEncodingKey(null);
  }, []);

  const ensureExport = useCallback((q) => {
    const cached = cachedExportRef.current[q.key];
    if (cached) return Promise.resolve(cached);
    const existing = inFlightExportRef.current[q.key];
    if (existing) return existing;
    const myToken = exportSessionRef.current;
    setEncodingKey(q.key);
    setEncodeProgress(0);
    const onProg = (p) => {
      if (exportSessionRef.current === myToken) setEncodeProgress(p);
    };
    const promise = exportVideo(q.w, onProg);
    inFlightExportRef.current[q.key] = promise;
    promise
      .then((result) => {
        if (exportSessionRef.current === myToken) {
          cachedExportRef.current[q.key] = result;
        }
      })
      .catch(() => { })
      .finally(() => {
        if (inFlightExportRef.current[q.key] === promise) {
          delete inFlightExportRef.current[q.key];
        }
        if (exportSessionRef.current === myToken) {
          setEncodeProgress(null);
          setEncodingKey(null);
        }
      });
    return promise;
  }, [exportVideo]);

  const handlePlayClick = useCallback(() => {
    invalidateExportCache();
    play();
  }, [invalidateExportCache, play]);

  // Inputs that affect the rendered animation — invalidate any cached export when they change.
  useEffect(() => {
    invalidateExportCache();
  }, [concept1, concept2, tech, invalidateExportCache]);

  const baseFilename = `fission-${concept1}-${concept2}-${tech}`;
  const encodingQuality = QUALITIES.find((q) => q.key === encodingKey);

  const exportNow = useCallback(async (q) => {
    setBusy(true);
    try {
      const { blob, ext } = await ensureExport(q);
      const gUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = gUrl;
      a.download = `${baseFilename}-${q.w}px.${ext}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(gUrl), 1000);
    } catch (e) {
      flashStatus(e?.message || "encoding failed");
    } finally {
      setBusy(false);
    }
  }, [ensureExport, flashStatus, baseFilename]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    const ctx = canvas.getContext("2d");
    initState(rect.width, rect.height);
    const s = st.current;
    s.c1o = 1; s.c2o = 1; s.to = 1;
    s.c1to = 1; s.c2to = 1; s.tto = 1;
    paint(ctx, rect.width, rect.height);
  }, [paint, initState]);

  const inputStyle = {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 6,
    padding: "0.55rem 0.8rem",
    color: "#d8d8e4",
    fontSize: "1rem",
    fontWeight: 300,
    width: 150,
    outline: "none",
    fontFamily: "inherit",
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#050510",
      color: "#d0d0dc",
      fontFamily: '"Segoe UI", "Helvetica Neue", sans-serif',
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "2.5rem 1rem 2rem",
    }}>
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <h1 style={{
          fontSize: "clamp(1.5rem, 4vw, 2.4rem)",
          fontWeight: 200,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "#b0b0c4",
          margin: 0,
        }}>
          Concept Fission
        </h1>
        <a
          href="https://www.youtube.com/watch?v=1CbmC2aWhjY"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: "0.8rem",
            color: "#50506a",
            fontWeight: 300,
            letterSpacing: "0.06em",
            marginTop: "0.4rem",
            display: "inline-block",
            textDecoration: "none",
            borderBottom: "1px solid rgba(80,80,106,0.4)",
            paddingBottom: 1,
          }}
        >
          from "Intelligence is Artificial" ↗
        </a>
      </div>

      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "0.8rem",
        marginBottom: "1.2rem",
      }}>
        {[
          [{ label: "technology", val: tech, set: setTech }],
          [
            { label: "concept 1", val: concept1, set: setConcept1 },
            { label: "concept 2", val: concept2, set: setConcept2 },
          ],
        ].map((row, ri) => (
          <div key={ri} style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center" }}>
            {row.map((f, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                <label style={{
                  fontSize: "0.65rem",
                  color: "#555568",
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  fontWeight: 500,
                }}>
                  {f.label}
                </label>
                <input
                  value={f.val}
                  onChange={(e) => f.set(e.target.value)}
                  style={inputStyle}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: "0.6rem", marginBottom: "0.5rem", flexWrap: "wrap", justifyContent: "center" }}>
        <button
          onClick={handlePlayClick}
          disabled={phase === "playing" || busy}
          style={{
            background: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 8,
            padding: "0.55rem 1.6rem",
            color: (phase === "playing" || busy) ? "#444" : "#c8c8d8",
            fontSize: "0.82rem",
            fontWeight: 400,
            letterSpacing: "0.06em",
            cursor: (phase === "playing" || busy) ? "not-allowed" : "pointer",
            fontFamily: "inherit",
          }}
        >
          {phase === "playing" ? "Playing..." : "Play"}
        </button>
        {QUALITIES.map((q) => {
          const isEncoding = encodingKey === q.key;
          return (
            <button
              key={q.key}
              onClick={() => exportNow(q)}
              disabled={busy}
              title={`Download ${q.w}×${q.w} video`}
              style={{
                background: "rgba(180,180,255,0.12)",
                border: "1px solid rgba(180,180,255,0.22)",
                borderRadius: 8,
                padding: "0.5rem 1.2rem",
                color: busy ? "#666" : "#e2e2f0",
                letterSpacing: "0.06em",
                cursor: busy ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.15rem",
                minWidth: 96,
              }}
            >
              <span style={{ fontSize: "0.82rem", fontWeight: 500 }}>Download</span>
              <span style={{ fontSize: "0.6rem", fontWeight: 400, opacity: 0.55 }}>
                {isEncoding ? "encoding…" : `${q.w}px`}
              </span>
            </button>
          );
        })}
      </div>
      <div style={{
        minHeight: "1.1rem",
        marginBottom: "1rem",
        width: "min(90vw, 540px)",
        display: "flex",
        alignItems: "center",
        gap: "0.7rem",
      }}>
        {encodeProgress !== null && (
          <>
            <div style={{
              flex: 1,
              height: 6,
              borderRadius: 999,
              background: "rgba(255,255,255,0.07)",
              overflow: "hidden",
            }}>
              <div style={{
                height: "100%",
                width: `${Math.round(encodeProgress * 100)}%`,
                borderRadius: 999,
                background: "linear-gradient(90deg, rgba(150,150,235,0.8), rgba(200,200,255,0.95))",
                boxShadow: "0 0 8px rgba(180,180,255,0.5)",
                transition: "width 0.15s ease-out",
              }} />
            </div>
            <span style={{
              fontSize: "0.72rem",
              color: "#9090b0",
              letterSpacing: "0.04em",
              fontVariantNumeric: "tabular-nums",
              minWidth: "6rem",
              textAlign: "right",
            }}>
              {encodingQuality ? `${encodingQuality.label} · ` : ""}{Math.round(encodeProgress * 100)}%
            </span>
          </>
        )}
      </div>
      {statusMsg && (
        <div style={{
          position: "fixed",
          bottom: "2rem",
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(20,20,30,0.95)",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 8,
          padding: "0.7rem 1.1rem",
          fontSize: "0.8rem",
          color: "#e0e0ec",
          letterSpacing: "0.04em",
          zIndex: 1100,
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          maxWidth: "90vw",
          textAlign: "center",
        }}>
          {statusMsg}
        </div>
      )}

      <canvas
        ref={canvasRef}
        style={{
          width: "min(90vw, 540px)",
          aspectRatio: "1 / 1",
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.04)",
        }}
      />

    </div>
  );
}